<script setup>
  import { computed } from "vue";
  import { Card, CardContent } from "@/components/ui/card";
  import { Button } from "@/components/ui/button";
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
  } from "@/components/ui/dropdown-menu";
  import { FileText, Pen, Download, MoreVertical, Copy, Trash2 } from "lucide-vue-next";

  const props = defineProps({
    report: {
      type: Object,
      required: true,
    },
  });

  defineEmits(["click", "download", "duplicate", "delete"]);

  const previewUrl = computed(() => {
    const b64 = props.report.report_theme_backgrounds?.cover?.preview_base64;
    return b64 ? `data:image/png;base64,${b64}` : null;
  });

  const hasSlides = computed(() => (props.report.slides_count || props.report.slides?.length || 0) > 0);
</script>

<template>
  <Card
    class="group cursor-pointer transition-all hover:shadow-md hover:border-primary/50 overflow-hidden !py-0 !gap-0"
    @click="$emit('click')">
    <CardContent class="p-0">
      <!-- Preview Area -->
      <div class="aspect-video bg-muted relative overflow-hidden">
        <img
          v-if="previewUrl"
          :src="previewUrl"
          :alt="report.name"
          class="w-full h-full object-cover" />
        <div
          v-else
          class="w-full h-full flex items-center justify-center">
          <FileText class="w-10 h-10 text-muted-foreground/50" />
        </div>

        <!-- Overlay with actions -->
        <div
          class="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <Button
            size="sm"
            variant="secondary"
            @click.stop="$emit('click')">
            <Pen class="w-3.5 h-3.5 mr-1" />
            Edit
          </Button>
        </div>
      </div>

      <!-- Info -->
      <div class="p-3">
        <div class="flex items-center justify-between">
          <div class="flex-1 min-w-0">
            <span class="text-sm font-medium truncate block">{{ report.name }}</span>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger as-child>
              <Button
                variant="ghost"
                size="icon"
                class="h-7 w-7"
                @click.stop>
                <MoreVertical class="w-3.5 h-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                :disabled="!hasSlides"
                @click.stop="$emit('download')">
                <Download class="w-3.5 h-3.5 mr-2" />
                Download
              </DropdownMenuItem>
              <DropdownMenuItem @click.stop="$emit('duplicate')">
                <Copy class="w-3.5 h-3.5 mr-2" />
                Duplicate
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                class="text-destructive"
                @click.stop="$emit('delete')">
                <Trash2 class="w-3.5 h-3.5 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </CardContent>
  </Card>
</template>
